import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../context/AuthContext';
import ProductCard from '../components/ProductCard';
import { Filter, X, ChevronDown } from 'lucide-react';

const PROVINCES = ['SAN_JOSE', 'ALAJUELA', 'HEREDIA', 'CARTAGO', 'GUANACASTE', 'PUNTARENAS', 'LIMON'];
const CONDITIONS = ['NEW', 'USED'];

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [brands, setBrands] = useState([]);

  const filters = {
    search: searchParams.get('search') || '',
    brand: searchParams.get('brand') || '',
    model: searchParams.get('model') || '',
    condition: searchParams.get('condition') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    province: searchParams.get('province') || '',
    sort: searchParams.get('sort') || 'newest'
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [searchParams]);

  const fetchBrands = async () => {
    try {
      const apiUrl = `/api`;
      const res = await fetch(`${apiUrl}/brands/visible`);
      const data = await res.json();
      setBrands(data.brands || []);
    } catch {
      setBrands([]);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ is_part: 'true' });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await fetch(`/api/products?${params.toString()}`);
      const data = await response.json();
      setProducts(data.products || []);
      setPagination(data.pagination || { page: 1, pages: 1, total: data.products?.length || 0 });
    } catch {} finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const clearFilters = () => setSearchParams({});

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white border-b sticky top-16 md:top-32 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">
                {filters.search ? `Resultados: "${filters.search}"` : 
                 filters.brand ? `${filters.brand} - Repuestos` : 
                 'Todos los Repuestos'}
              </h1>
              <p className="text-sm text-gray-500">{pagination.total} productos</p>
            </div>
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center gap-2 px-4 py-2 border rounded-full"
            >
              <Filter className="w-4 h-4" /> Filtros
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar Filters */}
          <aside className={`${showFilters ? 'fixed inset-0 z-50 bg-white p-4 overflow-auto' : 'hidden'} md:block md:w-64 shrink-0`}>
            <div className="md:sticky md:top-40">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold">Filtros</h3>
                {(filters.search || filters.brand || filters.condition || filters.province) && (
                  <button onClick={clearFilters} className="text-sm text-primary">Limpiar</button>
                )}
              </div>

              {/* Brand */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Marca</label>
                <select 
                  value={filters.brand}
                  onChange={(e) => updateFilter('brand', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Todas las marcas</option>
                  {brands.map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              {/* Condition */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Condición</label>
                <div className="space-y-2">
                  {CONDITIONS.map(c => (
                    <label key={c} className="flex items-center gap-2">
                      <input 
                        type="radio" 
                        name="condition"
                        checked={filters.condition === c}
                        onChange={() => updateFilter('condition', c)}
                        className="text-primary"
                      />
                      <span className="text-sm">{c === 'NEW' ? 'Nuevo' : 'Usado'}</span>
                    </label>
                  ))}
                  <label className="flex items-center gap-2">
                    <input 
                      type="radio" 
                      name="condition"
                      checked={!filters.condition}
                      onChange={() => updateFilter('condition', '')}
                      className="text-primary"
                    />
                    <span className="text-sm">Todos</span>
                  </label>
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Rango de precio</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="number" 
                    placeholder="Mín"
                    value={filters.min_price}
                    onChange={(e) => updateFilter('min_price', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                  <span>-</span>
                  <input 
                    type="number" 
                    placeholder="Máx"
                    value={filters.max_price}
                    onChange={(e) => updateFilter('max_price', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              {/* Province */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Ubicación</label>
                <select 
                  value={filters.province}
                  onChange={(e) => updateFilter('province', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Todas las provincias</option>
                  {PROVINCES.map(p => (
                    <option key={p} value={p}>{p.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>

              {showFilters && (
                <button 
                  onClick={() => setShowFilters(false)}
                  className="w-full bg-primary text-white py-3 rounded-full mt-4 md:hidden"
                >
                  Aplicar Filtros
                </button>
              )}
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Sort bar */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex gap-2 flex-wrap">
                {filters.brand && (
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm flex items-center gap-1">
                    {filters.brand}
                    <button onClick={() => updateFilter('brand', '')}><X className="w-3 h-3" /></button>
                  </span>
                )}
                {filters.condition && (
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm flex items-center gap-1">
                    {filters.condition === 'NEW' ? 'Nuevo' : 'Usado'}
                    <button onClick={() => updateFilter('condition', '')}><X className="w-3 h-3" /></button>
                  </span>
                )}
                {filters.province && (
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm flex items-center gap-1">
                    {filters.province.replace('_', ' ')}
                    <button onClick={() => updateFilter('province', '')}><X className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
              <select 
                value={filters.sort}
                onChange={(e) => updateFilter('sort', e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="newest">Más recientes</option>
                <option value="price_asc">Precio: menor a mayor</option>
                <option value="price_desc">Precio: mayor a menor</option>
                <option value="popular">Más populares</option>
              </select>
            </div>

            {/* Products */}
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl overflow-hidden">
                    <div className="aspect-square bg-gray-200 animate-pulse" />
                    <div className="p-4 space-y-2">
                      <div className="h-4 bg-gray-200 rounded animate-pulse" />
                      <div className="h-6 bg-gray-200 rounded w-2/3 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-bold mb-2">No hay productos</h3>
                <p className="text-gray-500 mb-4">Intenta con otros filtros o busca algo diferente.</p>
                <button onClick={clearFilters} className="bg-primary text-white px-6 py-2 rounded-full">
                  Limpiar Filtros
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {products.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    {[...Array(Math.min(5, pagination.pages))].map((_, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          const newParams = new URLSearchParams(searchParams);
                          newParams.set('page', (i + 1).toString());
                          setSearchParams(newParams);
                        }}
                        className={`w-10 h-10 rounded-full ${pagination.page === i + 1 ? 'bg-primary text-white' : 'bg-white border'}`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}